const FIRST_TOUCH_KEY = 'acquisition_first_touch';
const LAST_TOUCH_KEY = 'acquisition_last_touch';
const SESSION_KEY = 'acquisition_session';

const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  'campaign_id',
  'adset_id',
  'ad_id',
  'creative_hook',
  'fbclid',
  'gclid',
  'ttclid',
] as const;

export type AttributionContext = Record<string, string | null> & {
  landing?: string | null;
  theme?: string | null;
  captured_at?: string | null;
};

function parseStored(key: string): AttributionContext {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as AttributionContext) : {};
  } catch {
    window.localStorage.removeItem(key);
    return {};
  }
}

export function captureAttribution(input?: { landing?: string; theme?: string }) {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  const captured: AttributionContext = {
    landing: input?.landing ?? window.location.pathname,
    theme: input?.theme ?? null,
    captured_at: new Date().toISOString(),
  };

  for (const key of ATTRIBUTION_KEYS) {
    captured[key] = params.get(key);
  }

  const hasCampaignSignal = ATTRIBUTION_KEYS.some((key) => Boolean(captured[key]));
  const session = { ...parseStored(SESSION_KEY), ...captured };
  window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));

  if (hasCampaignSignal) {
    if (!window.localStorage.getItem(FIRST_TOUCH_KEY)) {
      window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(captured));
    }
    window.localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(captured));
  }

  return getAttributionContext();
}

export function getAttributionContext(): AttributionContext {
  if (typeof window === 'undefined') {
    return {};
  }
  let session: AttributionContext = {};
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    session = raw ? (JSON.parse(raw) as AttributionContext) : {};
  } catch {
    window.sessionStorage.removeItem(SESSION_KEY);
  }
  const first = parseStored(FIRST_TOUCH_KEY);
  const last = parseStored(LAST_TOUCH_KEY);
  return {
    ...last,
    ...session,
    first_touch_source: first.utm_source ?? null,
    first_touch_campaign: first.utm_campaign ?? null,
    last_touch_source: last.utm_source ?? null,
    last_touch_campaign: last.utm_campaign ?? null,
  };
}
